import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useContext,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { IconButton } from '@material-ui/core';
import {
  Videocam,
  Close,
  CalendarTodayOutlined,
  EventOutlined,
  ScheduleOutlined,
  CategoryOutlined,
  DescriptionOutlined,
  PeopleOutline,
  VisibilityOutlined,
} from '@material-ui/icons';
import Skeleton from 'react-placeholder';
import SessionApi from '../session-api';
import Tabs from '../../../components/common/Tabs';
import Modal from '../../../components/common/Modal';
import Alert from '../../../components/common/Alert';
import MeetReport from './MeetReport';
import SurveyResults from './SurveyResults';
import MentorLog from './MentorLog';
import AutomationSummary from './AutomationSummary';
import AssignDateByMentor from './AssignDateByMentor';
import RescheduleHistory from './RescheduleHistory';
import { AppContext } from '../../../app/AppContext';
import { formatDate } from '../../../utils/format';
import { statusSession } from '../../../utils/constants';

const EMPTY_VALUE = '—';
const AUTOMATION_SUMMARY_TAB_ID = 'automation-summary';
const CONTENT_SCROLL_DELAY = 280;
const CONTENT_SCROLL_DURATION = 650;
const CONTENT_SCROLL_OFFSET = 24;
const CARD_REFLOW_DURATION = 480;
const CARD_REFLOW_STAGGER = 40;
const SCROLL_INTERRUPT_KEYS = [
  'ArrowUp',
  'ArrowDown',
  'PageUp',
  'PageDown',
  'Home',
  'End',
  'Tab',
  ' ',
];

const easeInOutCubic = (progress) =>
  progress < 0.5
    ? 4 * progress * progress * progress
    : 1 - Math.pow(-2 * progress + 2, 3) / 2;

const captureLayoutPosition = (element) => {
  if (!element) {
    return null;
  }

  const { left, top, width, height } = element.getBoundingClientRect();

  return { element, left, top, width, height };
};

const formatSessionDate = (date) => {
  return date ? formatDate(date, 'DD/MM/YYYY HH:mm') : EMPTY_VALUE;
};

const getParticipantInitial = (name) => {
  return name && name.trim() ? name.trim().charAt(0).toUpperCase() : '?';
};

const SessionDetailsContainer = () => {
  const history = useHistory();
  const location = useLocation();
  const {
    settings: { settings },
  } = useContext(AppContext);
  const isMountedRef = useRef(null);
  const summaryCardRef = useRef(null);
  const participantsCardRef = useRef(null);
  const contentRef = useRef(null);
  const contentScrollTargetRef = useRef(null);
  const shouldScrollContentRef = useRef(false);
  const pendingLayoutPositionsRef = useRef(null);
  const [session, setSession] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [mentor, setMentor] = useState({});
  const [mentee, setMentee] = useState({});
  const [surveyResponses, setSurveyResponses] = useState([]);
  const [rescheduleHistory, setRescheduleHistory] = useState([]);
  const [typeAlert, setTypeAlert] = useState('info');
  const [messageAlert, setMessageAlert] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [refreshSession, setRefreshSession] = useState(0);
  const [isAutomationSummaryActive, setIsAutomationSummaryActive] = useState(
    false
  );

  useEffect(() => {
    if (!messageAlert && typeAlert === 'info') {
      return undefined;
    }

    const alertTimeout = setTimeout(() => {
      setMessageAlert('');
      setTypeAlert('info');
    }, 5000);

    return () => clearTimeout(alertTimeout);
  }, [messageAlert, typeAlert]);

  useLayoutEffect(() => {
    const previousPositions = pendingLayoutPositionsRef.current;
    pendingLayoutPositionsRef.current = null;

    if (!previousPositions?.length) {
      return undefined;
    }

    if (contentRef.current) {
      contentScrollTargetRef.current = Math.max(
        0,
        contentRef.current.getBoundingClientRect().top +
          (window.pageYOffset || document.documentElement.scrollTop) -
          CONTENT_SCROLL_OFFSET
      );
    }

    const prefersReducedMotion =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      return undefined;
    }

    const animations = previousPositions
      .map(({ element, left, top, width, height }, index) => {
        if (!element || typeof element.animate !== 'function') {
          return null;
        }

        const currentPosition = element.getBoundingClientRect();
        const horizontalMovement = left - currentPosition.left;
        const verticalMovement = top - currentPosition.top;
        const hasSizeChange =
          Math.abs(currentPosition.width - width) >= 1 ||
          Math.abs(currentPosition.height - height) >= 1;

        if (
          Math.abs(horizontalMovement) < 1 &&
          Math.abs(verticalMovement) < 1 &&
          !hasSizeChange
        ) {
          return null;
        }

        return element.animate(
          [
            {
              transform: `translate3d(${horizontalMovement}px, ${verticalMovement}px, 0) scale(${hasSizeChange ? 0.985 : 1})`,
              transformOrigin: 'top left',
            },
            {
              transform: 'translate3d(0, 0, 0) scale(1)',
              transformOrigin: 'top left',
            },
          ],
          {
            duration: CARD_REFLOW_DURATION,
            delay: index * CARD_REFLOW_STAGGER,
            easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
            fill: 'backwards',
          }
        );
      })
      .filter(Boolean);

    return () => animations.forEach((animation) => animation.cancel());
  }, [isAutomationSummaryActive]);

  useEffect(() => {
    isMountedRef.current = true;
    const sessionId = location.state && location.state.idSession;

    if (sessionId) {
      SessionApi.getDetailsSession(sessionId).then(
        (response) => {
          if (isMountedRef.current) {
            const data = {
              id: response.data.id,
              title: response.data.title,
              description: response.data.description,
              dateStart: response.data.start,
              dateEnd: response.data.end,
              status: response.data.status,
              pillar: response.data.pillar?.name || '',
              created: response.data.createdAt,
              cancelReason: response.data.cancelReason,
              cancelUser: response.data.cancelUser?.name || '',
              mentorLog: response.data.mentorLog,
              mentorLogSummary: response.data.mentorLogSummary,
              canViewMentorLog:
                Object.prototype.hasOwnProperty.call(
                  response.data,
                  'mentorLog'
                ) ||
                Object.prototype.hasOwnProperty.call(
                  response.data,
                  'mentorLogSummary'
                ),
            };

            const users = Array.isArray(response.data.users)
              ? response.data.users
              : [];
            const currentMentor = users.find(
              (user) => user.role?.type === 'mentor'
            );
            const currentMentee =
              users.find((user) =>
                ['mentee', 'none'].includes(user.role?.type)
              ) || users.find((user) => user.role?.type !== 'mentor');

            setMentor(currentMentor || {});
            setMentee(currentMentee || {});
            setSurveyResponses(
              Array.isArray(response.data.surveyResponses)
                ? response.data.surveyResponses
                : []
            );
            setRescheduleHistory(
              Array.isArray(response.data.rescheduleHistory)
                ? response.data.rescheduleHistory
                : []
            );
            setSession(data);
            setIsLoading(false);
          }
        }
      );
    } else {
      history.push('/reuniones');
    }

    return () => {
      isMountedRef.current = false;
    };
  }, [location, history, refreshSession]);

  useEffect(() => {
    if (!shouldScrollContentRef.current || !contentRef.current) {
      return undefined;
    }

    shouldScrollContentRef.current = false;
    const prefersReducedMotion =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      const targetScrollTop = contentScrollTargetRef.current;
      contentScrollTargetRef.current = null;

      if (targetScrollTop !== null) {
        window.scrollTo(window.pageXOffset, targetScrollTop);
      } else {
        contentRef.current.scrollIntoView({ behavior: 'auto', block: 'start' });
      }

      return undefined;
    }

    let animationFrame;
    let scrollTimeout;

    const removeScrollInterruptionListeners = () => {
      window.removeEventListener('wheel', interruptAnimatedScroll);
      window.removeEventListener('touchstart', interruptAnimatedScroll);
      window.removeEventListener('pointerdown', interruptAnimatedScroll);
      window.removeEventListener('keydown', handleScrollKeyDown);
    };

    const cancelAnimatedScroll = () => {
      window.clearTimeout(scrollTimeout);

      if (animationFrame) {
        window.cancelAnimationFrame(animationFrame);
      }

      removeScrollInterruptionListeners();
    };

    const interruptAnimatedScroll = () => {
      contentScrollTargetRef.current = null;
      cancelAnimatedScroll();
    };

    const handleScrollKeyDown = (event) => {
      if (SCROLL_INTERRUPT_KEYS.includes(event.key)) {
        interruptAnimatedScroll();
      }
    };

    window.addEventListener('wheel', interruptAnimatedScroll, {
      passive: true,
    });
    window.addEventListener('touchstart', interruptAnimatedScroll, {
      passive: true,
    });
    window.addEventListener('pointerdown', interruptAnimatedScroll, {
      passive: true,
    });
    window.addEventListener('keydown', handleScrollKeyDown);

    scrollTimeout = window.setTimeout(() => {
      if (!contentRef.current) {
        contentScrollTargetRef.current = null;
        removeScrollInterruptionListeners();
        return;
      }

      const initialScrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const targetScrollTop =
        contentScrollTargetRef.current ??
        Math.max(
          0,
          contentRef.current.getBoundingClientRect().top +
            initialScrollTop -
            CONTENT_SCROLL_OFFSET
        );
      contentScrollTargetRef.current = null;
      const scrollDistance = targetScrollTop - initialScrollTop;
      const animationStart = window.performance.now();

      const animateScroll = (timestamp) => {
        const progress = Math.min(
          (timestamp - animationStart) / CONTENT_SCROLL_DURATION,
          1
        );

        window.scrollTo(
          window.pageXOffset,
          initialScrollTop + scrollDistance * easeInOutCubic(progress)
        );

        if (progress < 1) {
          animationFrame = window.requestAnimationFrame(animateScroll);
        } else {
          removeScrollInterruptionListeners();
        }
      };

      animationFrame = window.requestAnimationFrame(animateScroll);
    }, CONTENT_SCROLL_DELAY);

    return () => {
      cancelAnimatedScroll();
    };
  }, [isAutomationSummaryActive]);

  const handleChangeSession = (type, message) => {
    setTypeAlert(type);
    setMessageAlert(message);
    setRefreshSession((currentValue) => currentValue + 1);
    handleChangeShowModal(false);
  };

  const handleChangeShowModal = (bool) => {
    setShowModal(bool);
  };

  const hasRescheduleHistory = (rescheduleHistory || []).length > 0;

  const tabItems = [
    {
      title: 'Resumen de Google Meet',
      content: (
        <MeetReport sessionId={session.id} startTime={session.dateStart} />
      ),
    },
    {
      title: 'Resultados de encuesta',
      content: <SurveyResults surveyResponses={surveyResponses} />,
    },
  ];

  if (session.id) {
    tabItems.push({
      id: AUTOMATION_SUMMARY_TAB_ID,
      title: 'Resumen de envíos/recordatorios/evaluaciones',
      content: (
        <AutomationSummary
          key={session.id}
          participants={[mentor, mentee].filter((person) => person.id)}
          sessionId={session.id}
        />
      ),
    });
  }

  if (session.status === 'finished' && session.canViewMentorLog) {
    tabItems.splice(1, 0, {
      title: 'Bitácora del mentor',
      content: (
        <MentorLog
          mentorLog={session.mentorLog}
          mentorLogSummary={session.mentorLogSummary}
          mentorName={mentor.name}
        />
      ),
    });
  }

  if (hasRescheduleHistory) {
    tabItems.push({
      title: 'Historial de reagendamientos',
      content: <RescheduleHistory historyItems={rescheduleHistory} />,
    });
  }

  const tabs = tabItems.map(({ id, title }) => ({ id, title }));
  const contentTabs = tabItems.map(({ content }) => content);
  const sessionStatus = statusSession[session.status] || 'Sin estado';
  const statusModifier = statusSession[session.status]
    ? session.status
    : 'default';

  const sessionFacts = [
    {
      label: 'Creada',
      value: formatSessionDate(session.created),
      icon: <CalendarTodayOutlined />,
    },
    {
      label: 'Inicio',
      value: formatSessionDate(session.dateStart),
      icon: <EventOutlined />,
      action:
        mentor.id && !settings.autoAssignSession ? (
          <Modal
            buttonName="Editar fecha"
            type="link"
            title="Seleccione nueva fecha de reunión"
            onOpen={showModal}
            onChange={handleChangeShowModal}
            content={
              <AssignDateByMentor
                idMentor={mentor.id}
                idSession={session.id}
                onChange={handleChangeSession}
              />
            }
          />
        ) : null,
    },
    {
      label: 'Término',
      value: formatSessionDate(session.dateEnd),
      icon: <ScheduleOutlined />,
    },
    {
      label: 'Pilar',
      value: session.pillar || EMPTY_VALUE,
      icon: <CategoryOutlined />,
    },
  ];

  const participants = [
    { role: 'Mentor', person: mentor },
    { role: 'Mentee', person: mentee },
  ];

  const handleViewParticipant = (person) => {
    if (!person?.id) {
      return;
    }

    history.push('/usuarios/editar', {
      idUser: person.id,
      roleType: person.role?.type,
    });
  };

  const handleContentTabChange = (tabIndex) => {
    const nextIsAutomationSummaryActive =
      tabs[tabIndex]?.id === AUTOMATION_SUMMARY_TAB_ID;

    if (nextIsAutomationSummaryActive !== isAutomationSummaryActive) {
      pendingLayoutPositionsRef.current = [
        captureLayoutPosition(summaryCardRef.current),
        captureLayoutPosition(participantsCardRef.current),
      ].filter(Boolean);
      shouldScrollContentRef.current = true;
      setIsAutomationSummaryActive(nextIsAutomationSummaryActive);
    }
  };

  return (
    <div className="session-detail">
      {messageAlert && (
        <div className="session-detail__alert">
          <Alert type={typeAlert}>{messageAlert}</Alert>
        </div>
      )}

      <header className="session-detail__hero">
        <div className="session-detail__hero-icon" aria-hidden="true">
          <Videocam />
        </div>
        <div className="session-detail__hero-copy">
          <span className="session-detail__eyebrow">Gestión de mentorías</span>
          <h1>
            {isLoading ? 'Cargando sesión…' : session.title || 'Sin asunto'}
          </h1>
          <p>
            Revisa la información, los participantes y el seguimiento de la
            sesión en un solo lugar.
          </p>
        </div>
        {!isLoading && (
          <span
            className={`session-detail__status session-detail__status--${statusModifier}`}
            role="status"
          >
            {sessionStatus}
          </span>
        )}
        <IconButton
          aria-label="Volver al listado de reuniones"
          title="Volver al listado de reuniones"
          className="session-detail__close"
          onClick={() => history.push('/reuniones')}
        >
          <Close />
        </IconButton>
      </header>

      {isLoading ? (
        <section
          className="session-detail__loading"
          aria-live="polite"
          aria-busy="true"
        >
          <p>Cargando información de la sesión…</p>
          <Skeleton showLoadingAnimation type="text" rows={12} />
        </section>
      ) : (
        <div
          className={`session-detail__layout${
            isAutomationSummaryActive
              ? ' session-detail__layout--automation'
              : ''
          }`}
        >
          <aside className="session-detail__sidebar">
            <section
              className="session-detail__card session-detail__card--summary"
              aria-labelledby="session-summary-title"
              ref={summaryCardRef}
            >
              <div className="session-detail__card-header">
                <div className="session-detail__section-icon" aria-hidden="true">
                  <DescriptionOutlined />
                </div>
                <div>
                  <span className="session-detail__eyebrow">Vista general</span>
                  <h2 id="session-summary-title">Resumen de la sesión</h2>
                </div>
              </div>

              <div className="session-detail__facts">
                {sessionFacts.map((fact) => (
                  <div className="session-detail__fact" key={fact.label}>
                    <div className="session-detail__fact-icon" aria-hidden="true">
                      {fact.icon}
                    </div>
                    <div className="session-detail__fact-copy">
                      <span>{fact.label}</span>
                      <strong>{fact.value}</strong>
                      {fact.action && (
                        <div className="session-detail__fact-action">
                          {fact.action}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="session-detail__description">
                <span>Descripción</span>
                <p>{session.description || 'Sin descripción registrada.'}</p>
              </div>

              {session.status === 'canceled' && (
                <div className="session-detail__cancellation" role="note">
                  <span>Información de cancelación</span>
                  <p>
                    <strong>Motivo:</strong>{' '}
                    {session.cancelReason || 'Sin motivo registrado.'}
                  </p>
                  <p>
                    <strong>Canceló:</strong>{' '}
                    {session.cancelUser || 'Usuario no disponible.'}
                  </p>
                </div>
              )}
            </section>

            <section
              className="session-detail__card"
              aria-labelledby="session-participants-title"
              ref={participantsCardRef}
            >
              <div className="session-detail__card-header">
                <div className="session-detail__section-icon" aria-hidden="true">
                  <PeopleOutline />
                </div>
                <div>
                  <span className="session-detail__eyebrow">Personas</span>
                  <h2 id="session-participants-title">Participantes</h2>
                </div>
              </div>
              <div className="session-detail__participants">
                {participants.map(({ role, person }) => (
                  <div
                    className={`session-detail__participant${
                      person.name ? '' : ' session-detail__participant--empty'
                    }`}
                    key={role}
                  >
                    <span
                      className="session-detail__participant-avatar"
                      aria-hidden="true"
                    >
                      {getParticipantInitial(person.name)}
                    </span>
                    <div>
                      <span>{role}</span>
                      <strong>
                        {person.name || `Sin ${role.toLowerCase()} asociado`}
                      </strong>
                    </div>
                    {person.id && (
                      <IconButton
                        className="session-detail__participant-view"
                        aria-label={`Ver información de ${person.name || role}`}
                        title={`Ver información de ${person.name || role}`}
                        onClick={() => handleViewParticipant(person)}
                      >
                        <VisibilityOutlined />
                      </IconButton>
                    )}
                  </div>
                ))}
              </div>
            </section>

          </aside>

          <section
            className="session-detail__content"
            aria-labelledby="session-content-title"
            ref={contentRef}
          >
            <div className="session-detail__content-header">
              <span className="session-detail__eyebrow">Seguimiento</span>
              <h2 id="session-content-title">Contenido de la sesión</h2>
              <p>
                Consulta el resumen de Google Meet y los registros asociados a
                la sesión.
              </p>
            </div>
            <div className="session-detail__tabs">
              <Tabs
                variant="scrollable"
                tabs={tabs}
                content={contentTabs}
                ariaLabel="Secciones del detalle de la sesión"
                onTabChange={handleContentTabChange}
              />
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default SessionDetailsContainer;
